import { Anchor, FormattedText } from "client/Components";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import { openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import { FileViewModel } from "client/Api/Documents/SharePoint/common";
import { ReactElement, useCallback } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { lowerCaseFirstCharacter } from "client/Misc/utils";
import { calculate as calculateSharePointOpenPossibilities } from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { downloadDocumentInline as downloadSharePointDocumentInline } from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { tryParseServerErrors } from "client/Validation/serverErrors";
import { ModelPath } from "client/Validation/path";
import { InvalidInputValidationResult } from "client/Validation/input";
import { saveAttachment, SaveAttachmentToSharePointResponseModel } from "client/Api/Email/sharePointAttachments";
import { AttachmentModel } from "client/Api/Email/email";
import { MailMessageLocationModel } from "client/Api/Email/common";
import { convertToMailMessageLocationQueryModel } from "client/Features/Email/common";
import { getSharePointLocationUrl } from "client/Features/Documents/routing";

export type SaveAttachmentHandlerOptions = {
	attachment: AttachmentModel;
	messageLocation: MailMessageLocationModel;
	onIsBusyChange(isBusy: boolean): void;
};

export function useSaveAttachmentHandler(options: SaveAttachmentHandlerOptions): () => void {
	const {
		attachment: { exchangeAttachmentId },
		messageLocation,
		onIsBusyChange
	} = options;

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const openFileFromSharepoint = useCallback(
		(file: FileViewModel): void => {
			const { canBeOpenedOnline } = calculateSharePointOpenPossibilities(file);
			if (canBeOpenedOnline) {
				openFileInWopi(file);
			} else {
				downloadSharePointDocumentInline(file);
			}
		},
		[]
	);

	const openSharepointFileLocation = useCallback(
		(file: FileViewModel): void => {
			const fileLocationUrl: string = getSharePointLocationUrl(file.location, file.parentFolderId);
			navigateTo(fileLocationUrl, true);
		},
		[]
	);

	const renderOpenSavedFileAlert = useCallback(
		(file: FileViewModel): ReactElement => {
			return (
				<FormattedText
					text={localize("_AttachmentSaved")}
					thingsToReplace={[
						{
							what: "{{OpenFile}}",
							filler: (
								<Anchor
									onClick={() => openFileFromSharepoint(file)}
								>
									{localize("_OpenFile")}
								</Anchor>
							)
						},
						{
							what: "{{OpenFileLocation}}",
							filler: (
								<Anchor
									onClick={() => openSharepointFileLocation(file)}
								>
									{lowerCaseFirstCharacter(localize("_OpenFileLocation"))}
								</Anchor>
							)
						}
					]}
				/>
			);
		},
		[
			openFileFromSharepoint,
			openSharepointFileLocation
		]
	);

	const saveAttachmentHandler = useCallback(
		async (): Promise<void> => {
			onIsBusyChange(true);
			try {
				const savedFileModel: SaveAttachmentToSharePointResponseModel = await saveAttachment(
					convertToMailMessageLocationQueryModel(messageLocation),
					exchangeAttachmentId,
					undefined,
					globalCancellation
				);
				onIsBusyChange(false);

				alertManager.showSuccess(
					renderOpenSavedFileAlert(savedFileModel.file),
					20
				);
			} catch (e) {

				if (isCancelledError(e)) {
					return;
				}

				const result: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(e);

				if (result.wasSuccessful && result.result.has(ModelPath.root)) {
					alertManager.showError(result.result.get(ModelPath.root)!.errorMessage);
				} else {
					alertManager.showError(
						localize("_AttachmentSaveFailed")
					);
				}
				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			exchangeAttachmentId,
			globalCancellation,
			messageLocation,
			onIsBusyChange,
			renderOpenSavedFileAlert
		]
	);

	return saveAttachmentHandler;
}